import { blueGrey } from "@mui/material/colors";
import {
  Box,
  CheckIcon,
  CloseIcon,
  CopyIcon,
  EditIcon,
  GKButton,
  GKModal,
  GKTextField,
  Grid2,
  IconButton,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from "components";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  useConfirmMutualFundAutoForwardMutation,
  useUpdateAdvisorAutoForwardMutation,
} from "store/features/SettingsApi";
import { GKModalProps } from "utils/commonInterface";

const ViewStepsModal = ({ open, setOpen }: GKModalProps) => {
  const form = useFormContext();

  const [isEditing, setIsEditing] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const [updatePassword] = useUpdateAdvisorAutoForwardMutation();
  const [confirmMutualFundAutoForward, { isLoading }] =
    useConfirmMutualFundAutoForwardMutation();

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const steps = [
    {
      label: "Login to your Google ID ",
      description: (
        <Typography fontWeight={600} fontSize={14} my={2}>
          Login to your Google ID Click the settings icon (top-right) and then
          click See all settings from the dropdown.
        </Typography>
      ),
    },
    {
      label: "Forwarding and POP/IMAP",
      description: (
        <Typography fontWeight={600} fontSize={14} my={2}>
          Next, go to the{" "}
          <strong
            style={{
              color: "var(--primary-900)",
            }}
          >
            Forwarding and POP/IMAP
          </strong>{" "}
          . Then click{" "}
          <strong
            style={{
              color: "var(--primary-900)",
            }}
          >
            add a forwarding address.
          </strong>
        </Typography>
      ),
    },
    {
      label: "Copy the email address",
      description: (
        <Typography fontWeight={600} fontSize={14} my={2}>
          Copy the email address mentioned above, paste it into Gmail: {""}
          <strong
            style={{
              color: "var(--primary-900)",
            }}
          >
            Add a forwarding address
          </strong>{" "}
          {""}
          field, click Next, confirm the pop-up, and click {""}
          <strong
            style={{
              color: "var(--primary-900)",
            }}
          >
            Proceed.
          </strong>
        </Typography>
      ),
    },
    {
      label: "Verify your permission",
      description: (
        <Typography fontWeight={600} fontSize={14} my={2}>
          Please click the link below to verify your permission. After clicking,
          you will be redirected to a confirmation page. Once confirmed, please
          return here and continue.
        </Typography>
      ),
    },
    {
      label: "Disable forwarding",
      description: (
        <Typography fontWeight={600} fontSize={14} my={2}>
          Next, make sure to select{" "}
          <strong
            style={{
              color: "var(--primary-900)",
            }}
          >
            Disable forwarding
          </strong>{" "}
          before proceeding. Once you have disabled forwarding, you can proceed
          to create the forwarding filter.
        </Typography>
      ),
    },
    {
      label: "Filter messages like these",
      description: (
        <Typography fontWeight={600} fontSize={14} my={2}>
          Next, return to your inbox and search for the email containing your
          file. Once you find it, click on the three dots in the top right
          corner of the email. Then select{" "}
          <strong
            style={{
              color: "var(--primary-900)",
            }}
          >
            Filter messages like these.
          </strong>
        </Typography>
      ),
    },
    {
      label: "Set Up Email Filters",
      description: (
        <Typography fontWeight={600} fontSize={14} my={2}>
          <li style={{ marginBottom: 2 }}>
            Add the senders email address in the{" "}
            <strong
              style={{
                color: "var(--primary-900)",
              }}
            >
              From
            </strong>{" "}
            field.
          </li>
          <li style={{ marginBottom: 2 }}>
            Under{" "}
            <strong
              style={{
                color: "var(--primary-900)",
              }}
            >
              Subject
            </strong>
            , enter keywords that are always part of the email subject.
          </li>
          <li style={{ marginBottom: 2 }}>
            Check{" "}
            <strong
              style={{
                color: "var(--primary-900)",
              }}
            >
              Has attachment
            </strong>{" "}
            to ensure that only emails that contain a file attachment are
            forwarded.
          </li>
          <p
            style={{
              fontSize: "1.125rem",
              marginTop: "8px",
              marginRight: "8px",
              fontWeight: "500",
            }}
          >
            Next, click on the
            <strong
              style={{
                color: "var(--primary-900)",
              }}
            >
              {" "}
              Create Filter{" "}
            </strong>
            option.
          </p>
        </Typography>
      ),
    },
    {
      label: "Select the ( Forward it to )",
      description: (
        <Typography fontWeight={600} fontSize={14} my={2}>
          Finally, select the ({" "}
          <strong
            style={{
              color: "var(--primary-900)",
            }}
          >
            Forward it to
          </strong>{" "}
          ) option and choose the correct auto-forward Gridkey email address
          from the dropdown. Then click{" "}
          <strong
            style={{
              color: "var(--primary-900)",
            }}
          >
            {" "}
            Create Filter{" "}
          </strong>{" "}
          . Your auto-forward rule is now set up for this template. Your future
          files will be automatically forwarded and imported to your Gridkey
          account.
        </Typography>
      ),
    },
  ];

  return (
    <GKModal
      open={open}
      width={"70%"}
      maxHeight="500px"
      setOpen={setOpen}
      modalTitle="Mutual Fund Setup"
      footer={
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1,
          }}
        >
          {activeStep !== 0 && (
            <GKButton variant="outlined" onClick={() => handleBack()}>
              Previous
            </GKButton>
          )}
          {activeStep !== 7 && (
            <GKButton
              loading={isLoading}
              variant="contained"
              onClick={async () => {
                try {
                  if (activeStep === 3) {
                    const response = await confirmMutualFundAutoForward(
                      form.getValues("id"),
                    ).unwrap();
                    const link = document.createElement("a");
                    link.href = response.data.code;
                    link.target = "_blank";
                    link.click();
                    handleNext();
                  } else {
                    handleNext();
                  }
                } catch (error) {
                  console.log(error);
                }
              }}
            >
              Next
            </GKButton>
          )}
          {activeStep === 7 && (
            <GKButton
              loading={isLoading}
              variant="contained"
              onClick={() => {
                setOpen(false);
              }}
            >
              Finish
            </GKButton>
          )}
        </Box>
      }
    >
      <Grid2 container rowGap={2} spacing={2}>
        <Grid2 size={6}>
          <Controller
            name="email"
            control={form.control}
            render={({ field }) => (
              <GKTextField
                {...field}
                inputLabel={"Email"}
                slotProps={{
                  input: {
                    readOnly: true,
                    endAdornment: (
                      <IconButton
                        onClick={() => {
                          navigator.clipboard.writeText(`${field.value}`);
                        }}
                      >
                        <CopyIcon />
                      </IconButton>
                    ),
                  },
                }}
              />
            )}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="password"
            control={form.control}
            render={({ field }) => (
              <GKTextField
                {...field}
                disabled={!isEditing}
                inputLabel={"Password"}
                placeholder="Enter Password"
                slotProps={{
                  input: {
                    endAdornment: isEditing ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <IconButton
                          color="success"
                          onClick={() => {
                            updatePassword({ password: field.value });
                            setIsEditing(false);
                          }}
                        >
                          <CheckIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => setIsEditing(false)}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    ) : (
                      <IconButton onClick={() => setIsEditing(true)}>
                        <EditIcon />
                      </IconButton>
                    ),
                  },
                }}
              />
            )}
          />
        </Grid2>
        <Grid2 size={5}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step) => (
              <Step key={step.label}>
                <StepLabel>
                  <Typography
                    sx={{ fontWeight: 600, fontSize: 12, color: blueGrey[700] }}
                  >
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>{step.description}</StepContent>
              </Step>
            ))}
          </Stepper>
        </Grid2>
        {activeStep === 0 && (
          <Grid2
            size={7}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"flex-start"}
            style={{
              padding: 10,
              borderRadius: 10,
            }}
          >
            <img
              src="/AutoImportPics/Gmail1.1.svg"
              style={{
                width: "100%",
                height: "auto",
              }}
              alt="step-1"
            />
          </Grid2>
        )}
        {activeStep === 1 && (
          <Grid2
            size={7}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"flex-start"}
            style={{
              padding: 10,
              borderRadius: 10,
            }}
          >
            {" "}
            <img
              src="/AutoImportPics/Gmail2.1.svg"
              style={{
                width: "100%",
                height: "auto",
              }}
              alt="step-1"
            />
          </Grid2>
        )}
        {activeStep === 2 && (
          <Grid2
            size={7}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"flex-start"}
            style={{
              padding: 10,
              borderRadius: 10,
            }}
          >
            <img
              src="/AutoImportPics/Gmail3.1.svg"
              style={{
                width: "100%",
                height: "auto",
              }}
              alt="step-1"
            />
          </Grid2>
        )}
        {activeStep === 3 && (
          <Grid2
            size={7}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"flex-start"}
            style={{
              padding: 10,
              borderRadius: 10,
            }}
          >
            <img
              src="/AutoImportPics/Gmail4.1.svg"
              style={{
                width: "100%",
                height: "auto",
              }}
              alt="step-1"
            />
          </Grid2>
        )}
        {activeStep === 4 && (
          <Grid2
            size={7}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"flex-start"}
            style={{
              padding: 10,
              borderRadius: 10,
            }}
          >
            <img
              src="/AutoImportPics/Gmail5.1.svg"
              style={{
                width: "100%",
                height: "auto",
              }}
              alt="step-1"
            />
          </Grid2>
        )}
        {activeStep === 5 && (
          <Grid2
            size={7}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"flex-start"}
            style={{
              padding: 10,
              borderRadius: 10,
            }}
          >
            {" "}
            <img
              src="/AutoImportPics/Gmail6.1.svg"
              style={{
                width: "100%",
                height: "auto",
              }}
              alt="step-1"
            />
          </Grid2>
        )}
        {activeStep === 6 && (
          <Grid2
            size={7}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"flex-start"}
            style={{
              padding: 10,
              borderRadius: 10,
            }}
          >
            <img
              src="/AutoImportPics/Gmail7.1.svg"
              style={{
                width: "100%",
                height: "auto",
              }}
              alt="step-1"
            />
          </Grid2>
        )}
        {activeStep === 7 && (
          <Grid2
            size={7}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"flex-start"}
            style={{
              padding: 10,
              borderRadius: 10,
            }}
          >
            <img
              src="/AutoImportPics/Gmail8.1.svg"
              style={{
                width: "100%",
                height: "auto",
              }}
              alt="step-1"
            />
          </Grid2>
        )}
      </Grid2>
    </GKModal>
  );
};

export default ViewStepsModal;
